/**
 * Sanitize rich-editor output so it's safe to round-trip through the
 * mobile app's renderer. Mobile reads either:
 *   - Plain text + Markdown
 *   - $$LaTeX$$ delimiters
 *   - <span class="math-equation">$$LaTeX$$</span> (legacy wrapper)
 *   - A small set of inline HTML tags it knows how to handle
 *
 * Everything else (style attributes, Google Docs / MS Word leftovers,
 * arbitrary <span> wrappers, contenteditable junk) gets stripped.
 */
import sanitizeHtml from 'sanitize-html';
import { wrapBareInlineMath } from './wrapBareInlineMath';

// Re-export so existing imports of { wrapBareInlineMath } from './sanitizeContent'
// keep working. The implementation lives in its own file because the backfill
// script needs it without pulling in `sanitize-html`.
export { wrapBareInlineMath };

// Mobile reads either plain text + Markdown, or $$LaTeX$$ delimiters, or the
// legacy <span class="math-equation">$$LaTeX$$</span> wrapper. The notes pipe
// strips remaining HTML via formatForMarkdown; the steps pipe does NOT — it
// passes content straight to a Markdown renderer that treats stray HTML as
// literal text. So we sanitize to that lowest-common-denominator: keep only
// the math-equation span wrapper, then post-process block tags into newlines
// so authors' visual line breaks survive as Markdown paragraph breaks.
const SANITIZE_OPTIONS = {
  // We allow block tags here only so sanitize-html *preserves their
  // children*. The post-processing pass below replaces them with newlines.
  // The only HTML tag that survives into the final output is
  // <span class="math-equation">.
  //
  // <b>/<i>/<u> are intentionally not allowed. We previously tried converting
  // them to Markdown ** / * but nested constructs like <i><b>X</b></i> produce
  // ambiguous "*X***" sequences the renderer can't parse — they leaked as
  // literal asterisks in prod. Stripping the tags entirely preserves the text
  // and loses only the styling, which is far better than visible markers.
  allowedTags: ['span', 'br', 'p', 'div', 'ul', 'ol', 'li'],
  allowedAttributes: {
    span: ['class'],
  },
  allowedClasses: {
    span: ['math-equation'],
  },
  transformTags: {
    // Keep only the math-equation span wrapper. Bare <span> (or any with a
    // non-allowlisted class) becomes plain text — sanitize-html drops the
    // tag but preserves the child text.
    span: (_tagName, attribs) =>
      attribs.class === 'math-equation'
        ? { tagName: 'span', attribs: { class: 'math-equation' } }
        : { tagName: '', attribs: {} },
  },
  // Discard MS Office / browser script tags entirely (content + tags).
  nonTextTags: ['style', 'script', 'textarea', 'option', 'noscript'],
  allowedSchemes: ['http', 'https', 'mailto'],
  parser: {
    decodeEntities: true,
  },
};

/**
 * Sanitize HTML coming out of the rich-text editor.
 * Empty/null input returns an empty string.
 */
export function sanitizeRichHtml(html) {
  if (!html) return '';
  let cleaned = sanitizeHtml(html, SANITIZE_OPTIONS);

  // Zero-width spaces from the equation insertion path break Markdown
  // renderers and serve no purpose at rest.
  cleaned = cleaned.replace(/​/g, '');

  // Strip bold/italic/underline tags — text is preserved, styling is lost.
  // See note on allowedTags above for why we don't convert to Markdown.
  cleaned = cleaned.replace(/<\/?(b|strong|i|em|u)\b[^>]*>/gi, '');

  // Convert surviving block-level tags into plain newlines so authors'
  // visual line breaks land as Markdown paragraph breaks. Order matters:
  // closing tags first so the opening tag's break doesn't double up.
  cleaned = cleaned
    .replace(/<\/(p|div|ul|ol)>\s*/gi, '\n\n')
    .replace(/<(p|div|ul|ol)[^>]*>\s*/gi, '\n\n')
    .replace(/<\/li>\s*/gi, '\n')
    .replace(/<li[^>]*>\s*/gi, '\n• ')
    .replace(/<br\s*\/?>\s*/gi, '\n')
    // Defensive: strip any remaining HTML tag that isn't our math-equation
    // wrapper (anchors, headings, table cells, etc.).
    .replace(/<(?!\/?span\b)[^>]+>/gi, '');

  // Tidy whitespace.
  cleaned = cleaned
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Wrap inline math so authors don't have to remember to delimit single
  // variables. Mobile then renders them in the same TeX font as $$...$$.
  cleaned = wrapBareInlineMath(cleaned);

  return cleaned;
}

/**
 * Extract a plain-text version of pasted HTML, preserving line breaks.
 * Used by the paste handler so a paste from Word / Docs / a styled web
 * page never injects style noise into the editor.
 */
export function pasteToPlainText(html) {
  if (!html) return '';
  // First strip everything except line-break-bearing tags, then convert
  // them to \n, then collapse repeated whitespace.
  const text = sanitizeHtml(html, {
    allowedTags: ['br', 'p', 'div', 'li'],
    allowedAttributes: {},
  })
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<br\s*\/?>(\s*)/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return text
    .replace(/​/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
